import { deepRedactSensitiveValues, sanitizeUrlForPublicSinks } from '../security/redaction';
import { normalizeRouteTemplate, type PublicObservability } from '../observability/metrics';

export type PublicTelemetryEvent = {
  name: string;
  pageId?: string;
  route?: string;
  durationMs?: number;
  ok?: boolean;
  context?: Record<string, unknown>;
};

export type PublicTelemetry = {
  emit(event: PublicTelemetryEvent): void;
};

export type PublicTelemetryOptions = {
  observability?: PublicObservability;
  win?: Window;
};

export function createPublicTelemetry(options: PublicTelemetryOptions = {}): PublicTelemetry {
  const win = options.win || window;
  return {
    emit(event) {
      const detail = sanitizeTelemetryEvent(event);
      win.dispatchEvent(new CustomEvent('quaom:public-telemetry', { detail }));
      emitMetricForTelemetryEvent(options.observability, detail);
    },
  };
}

export function sanitizeTelemetryEvent(event: PublicTelemetryEvent): PublicTelemetryEvent {
  return {
    ...event,
    route: event.route ? normalizeRouteTemplate(sanitizeUrlForPublicSinks(event.route)) : undefined,
    context: event.context ? sanitizeTelemetryContext(event.context) : undefined,
  };
}

export function sanitizeTelemetryContext(
  context: Record<string, unknown>,
): Record<string, unknown> {
  const redacted = deepRedactSensitiveValues(context) as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(redacted)) {
    if (isForbiddenObservabilityField(key)) continue;
    if (typeof value === 'string') {
      result[key] = normalizeRouteTemplate(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

function emitMetricForTelemetryEvent(
  observability: PublicObservability | undefined,
  event: PublicTelemetryEvent,
): void {
  if (!observability) return;
  const routeTemplate = event.route;
  const tags = {
    pageId: event.pageId,
    routeTemplate,
    ok: event.ok === true,
  };

  switch (event.name) {
    case 'public_runtime_boot':
      observability.duration('public_runtime_boot_duration_ms', event.durationMs || 0, tags);
      return;
    case 'public_runtime_no_page':
      observability.counter('public_runtime_no_page_total', tags);
      return;
    case 'public_runtime_no_root':
      observability.counter('public_runtime_no_root_total', tags);
      return;
    case 'public_runtime_error':
      observability.counter('public_runtime_error_total', tags);
      return;
    case 'public_page_mount_started':
      observability.counter('public_page_mount_started_total', tags);
      return;
    case 'public_page_mount_succeeded':
      observability.counter('public_page_mount_succeeded_total', tags);
      observability.duration('public_page_mount_duration_ms', event.durationMs || 0, tags);
      return;
    case 'public_page_mount_failed':
      observability.counter('public_page_mount_failed_total', tags);
      return;
    case 'public_hydration_read':
      observability.counter('public_hydration_read_total', tags);
      return;
    case 'public_hydration_failed':
      observability.counter('public_hydration_failed_total', tags);
      return;
    case 'public_router_transition_attempted':
      observability.counter('public_router_transition_attempted_total', tags);
      return;
    case 'public_router_transition_intercepted':
      observability.counter('public_router_transition_intercepted_total', tags);
      return;
    case 'public_router_transition_fallback':
      observability.counter('public_router_transition_fallback_total', tags);
      return;
    case 'public_router_transition_blocked':
      observability.counter('public_router_transition_blocked_total', tags);
      return;
    case 'public_web_vital_reported':
      observability.counter('public_web_vital_reported_total', tags);
      return;
    default:
      return;
  }
}

function isForbiddenObservabilityField(key: string): boolean {
  return /publicId|textId|body|rawBody|textBody|rawHtml|html|email|ipAddress|rawIpAddress|userAgent|fingerprint|href|fullHref|locationHref/i.test(
    key,
  );
}
